import { useCallback, useEffect, useState } from 'react'
import { useLang } from '../i18n'
import { lockNoticeKey, parseLockNotice, type LockNotice } from '../lib/lock-notice'

/** What the hook needs of a HocuspocusProvider: its event emitter (the 'stateless' event). */
export interface StatelessSource {
  on(event: 'stateless', fn: (data: { payload: string }) => void): unknown
  off(event: 'stateless', fn: (data: { payload: string }) => void): unknown
}

/** At most this many notices on screen; the oldest goes first. */
const MAX_NOTICES = 3

/**
 * The lock notices addressed to THIS member (AB#301): the room's stateless messages whose `holder`
 * is `userId`. Another member's notices are never kept, whatever the server sent — the holder, and
 * only the holder, is the one who can release. A newer notice over the same thing replaces the
 * older one. Cleared when the room (the source) changes.
 */
export function useLockNotices(source: StatelessSource | null, userId: string | null) {
  const [notices, setNotices] = useState<LockNotice[]>([])
  useEffect(() => {
    setNotices([])
    if (!source || !userId) return
    const onStateless = ({ payload }: { payload: string }): void => {
      const notice = parseLockNotice(payload)
      if (!notice || notice.holder.id !== userId) return
      const key = lockNoticeKey(notice)
      setNotices((prev) => [...prev.filter((n) => lockNoticeKey(n) !== key), notice].slice(-MAX_NOTICES))
    }
    source.on('stateless', onStateless)
    return () => {
      source.off('stateless', onStateless)
    }
  }, [source, userId])
  const dismiss = useCallback((notice: LockNotice): void => {
    const key = lockNoticeKey(notice)
    setNotices((prev) => prev.filter((n) => lockNoticeKey(n) !== key))
  }, [])
  const clear = useCallback((): void => setNotices([]), [])
  return { notices, dismiss, clear }
}

interface LockNoticesProps {
  notices: LockNotice[]
  /** Drop this member's lock in the open editor (leave the §, the cell, the selection). */
  onRelease: () => void
  onDismiss: (notice: LockNotice) => void
  /** A slide by its place in the deck ("Slide 2"). */
  slideName: (slide: string) => string
}

/**
 * Non-blocking notices for the lock holder: "Sofia's agent is waiting for §3 — release it when you
 * can", with Release (drops the lock) and Dismiss. A polite status region — it never steals focus —
 * whose buttons are plain, keyboard-reachable buttons.
 */
export function LockNotices({ notices, onRelease, onDismiss, slideName }: LockNoticesProps) {
  const { t } = useLang()
  const agentPattern = t('sec_lock_agent')
  const sentence = (n: LockNotice): string => {
    const raw = n.by.kind === 'agent' ? agentPattern.replace('{name}', () => n.by.name) : n.by.name
    const who = raw.charAt(0).toUpperCase() + raw.slice(1)
    const what = n.scope.slide
      ? t('lockn_sel').replace('{slide}', () => slideName(n.scope.slide!))
      : n.scope.cells
        ? t('lockn_cell')
        : n.scope.section
          ? t('lockn_sec').replace('{n}', () => n.scope.section!)
          : t('lockn_part')
    return t(n.type === 'lock-forced' ? 'lockn_forced' : 'lockn_wanted')
      .replace('{who}', () => who)
      .replace('{what}', () => what)
  }
  return (
    <div className="locknotices" role="status" aria-label={t('lockn_region')}>
      {notices.map((n) => (
        <div key={lockNoticeKey(n)} className={`locknotice ${n.type}`}>
          <span className="ic" aria-hidden="true">
            {n.type === 'lock-forced' ? '!' : '⏳'}
          </span>
          <p title={n.prompt}>{sentence(n)}</p>
          <button type="button" className="locknotice-release" onClick={onRelease}>
            {t('lockn_release')}
          </button>
          <button type="button" className="locknotice-dismiss" onClick={() => onDismiss(n)}>
            {t('lockn_dismiss')}
          </button>
        </div>
      ))}
    </div>
  )
}
